import { Component, Element, Event, EventEmitter, Host, Method, Prop, State, h } from '@stencil/core';
import { getAPI, type UserAPI } from '@sydle/ui-api';
import { syQuerySelector } from 'packages/sydle-ui-blog/src/utils/dom.utils';

@Component({
  tag: 'sy-blog-suggestion-modal',
  styleUrl: './scss/index.scss',
  shadow: true,
})
export class SyBlogSuggestionModal {
  @Element() el: HTMLSyBlogSuggestionModalElement;
  @State() message: string;

  /**
   * Determines if the suggestion modal is open.
   */
  @Prop({ reflect: true, mutable: true }) opened = false;

  /**
   * Title displayed in the suggestion modal.
   */
  @Prop() dialogTitle: string;

  /**
   * Placeholder text for the suggestion input field.
   */
  @Prop() dialogPlaceholder: string;

  /**
   * Text displayed on the send button in the suggestion modal.
   */
  @Prop() dialogSendMessage: string;

  /**
   * Message displayed in the body of the suggestion modal.
   */
  @Prop() dialogMessage: string;

  /**
   * Button send icon
   */
  @Prop() buttonSendIcon = 'send';

  /**
   * Classification for the suggestion ticket.
   */
  @Prop() ticketClassification: string;

  /**
   * Title of the response dialog after sending the suggestion.
   */
  @Prop() dialogResponseTitle: string;

  /**
   * Message in the response dialog after sending the suggestion.
   */
  @Prop() dialogResponseMessage: string;

  /**
   * Opens the suggestion modal.
   */
  @Method()
  async open(): Promise<void> {
    this.opened = true;
  }

  /**
   * Closes the suggestion modal.
   */
  @Method()
  async close(): Promise<void> {
    this.opened = false;
  }

  /**
   * Emitted after the send button is clicked.
   */
  @Event({ bubbles: true, composed: true }) sySignInWallOpen: EventEmitter<void>;

  private outsideClickHandler(event) {
    const isInsideClick = Boolean(
      (event?.composedPath() || []).find((p) => {
        if (p instanceof Node) {
          return this.el.contains(p);
        }
      }),
    );

    if (!isInsideClick && this.opened) {
      this.close();
    }
  }

  private addOutsideClickHandler() {
    const event = 'click';
    const handler = this.outsideClickHandler.bind(this);
    document.addEventListener(event, handler, { capture: true });
  }

  private handleMsgChange(event): void {
    this.message = event.target.value;
  }

  private sendSuggestion() {
    if (this.message) {
      const modal = syQuerySelector('sy-blog-alert-modal') as HTMLSyBlogSuggestionModalElement;
      window.blogFacade
        ?.sendSuggestion({ ticketClassification: this.ticketClassification, message: this.message })
        .then((response) => {
          if (response.error) {
            console.error(response.error);
          } else {
            modal.dialogTitle = this.dialogResponseTitle;
            modal.dialogMessage = this.dialogResponseMessage;
            modal.open();
            (this.el.shadowRoot?.querySelector('sy-textarea') as HTMLSyTextareaElement).value = '';
            this.message = '';
          }
        });
    }
  }

  private handleSentClick() {
    this.el.close();

    if (!getAPI<UserAPI>('userAPI')?.getUser()) {
      sessionStorage.setItem('suggestion', this.message);
      this.sySignInWallOpen.emit();
      return;
    }

    this.sendSuggestion();
  }

  disconnectedCallback() {
    if (this.el.isConnected) return;

    const event = 'click';
    const handler = this.outsideClickHandler.bind(this);

    document.removeEventListener(event, handler, { capture: true });
  }

  componentDidRender() {
    this.addOutsideClickHandler();
  }

  render() {
    return (
      <Host>
        <div id="suggestion-modal">
          <sy-text type="h4">{this.dialogTitle}</sy-text>
          <sy-text type="body" innerHTML={this.dialogMessage}></sy-text>
          <div class="textarea">
            <sy-textarea
              placeholder={this.dialogPlaceholder}
              onSyTextareaDidChange={(event) => this.handleMsgChange(event)}
            ></sy-textarea>
          </div>
          <div class="suggestion-send">
            <sy-button
              content="icon-right"
              variant="filled"
              color="secondary"
              icon={this.buttonSendIcon}
              label={this.dialogSendMessage}
              onclick={() => this.handleSentClick()}
            ></sy-button>
          </div>
        </div>
      </Host>
    );
  }
}
